import { useState, useEffect, useCallback } from "react";

export function useDocumentSync(sessionId) {
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  const addDocument = useCallback((file, documentType) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result;

      const document = {
        id: Math.random().toString(36).substring(2, 15),
        file: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          base64: base64Data,
        },
        documentType,
        uploadedAt: new Date().toISOString(),
      };

      setUploadedDocuments((prev) => [
        ...prev,
        {
          ...document,
          file: new File([file], file.name, { type: file.type }),
          uploadedAt: new Date(document.uploadedAt),
        },
      ]);
    };

    reader.readAsDataURL(file);
  }, []);

  const fetchUploadedFiles = useCallback(
    async (documentType) => {
      try {
        const response = await fetch(
          `${window.ajaxLink}cloud_dashboard/fetchTemporarilyPatientRegisterFiles`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId,
              documentType,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch uploaded file(s)");
        }

        const files = await response.json();
        const fileData = files?.responseData;

        if (
          !fileData.file_data ||
          !fileData.file_data.data ||
          !Array.isArray(fileData.file_data.data)
        ) {
          console.error("Invalid file_data format:", fileData.file_data);
          return;
        }

        const byteArray = new Uint8Array(fileData.file_data.data);
        const blob = new Blob([byteArray], { type: fileData.mime_type });

        const file = new File([blob], fileData.file_name, {
          type: fileData.mime_type,
          lastModified: new Date(fileData.uploaded_at).getTime(),
        });

        addDocument(file, documentType);
      } catch (error) {
        console.error("Error fetching uploaded file:", error);
      }
    },
    [sessionId, addDocument]
  );

  useEffect(() => {
    const eventSource = new EventSource(
      `${window.ajaxLink}updatesse/events?observeData=patientRegisterForm&valueName=${sessionId}`
    );

    eventSource.onmessage = (e) => {
      const docType = e.data?.toString();
      const validDocTypes = [
        "Primary Insurance Card",
        "Secondary Insurance Card",
        "Driver's License",
      ];

      if (validDocTypes.includes(docType)) {
        fetchUploadedFiles(docType);
      }
    };

    return () => {
      eventSource.close();
    };
  }, [sessionId, fetchUploadedFiles]);

  const removeDocument = useCallback((id) => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== id));
  }, []);

  const getDocumentsByType = useCallback(
    (documentType) => {
      return uploadedDocuments.filter(
        (doc) => doc.documentType === documentType
      );
    },
    [uploadedDocuments]
  );

  return {
    uploadedDocuments,
    setUploadedDocuments,
    addDocument,
    removeDocument,
    getDocumentsByType,
  };
}
